/**
 * MONSTER TOWN — GIFTS BETWEEN THE TWO TOWNS. FROZEN CONTRACT (src/core/*).
 * Additive only.
 *
 * ===========================================================================
 *  A GIFT NEVER WRITES TO THE OTHER KID'S SAVE. THIS IS THE WHOLE DESIGN.
 * ===========================================================================
 * The obvious implementation — "push the gift into my brother's inbox" — needs
 * a write path from one slot into the other, and the moment that path exists, a
 * bug in it can damage a town that its owner was not even playing. The expansion
 * brief forbids it ("a visitor cannot damage a town, by design, and this must be
 * guaranteed at the save layer").
 *
 * So the postbox works the other way round:
 *
 *   LEAVING A GIFT   writes an entry to MY OWN `gifts.outbox`, addressed to the
 *                    other slot. Only my slot is written. Always.
 *   RECEIVING ONE    happens on MY load: I READ the other slot (via
 *                    `save.peekSections`, which is read-only and hands back a
 *                    clone), take anything addressed to me that I have not
 *                    claimed before, and file it into MY OWN inbox. Only my slot
 *                    is written. Always.
 *
 * The result a kid sees is identical — "leave a gift on their doorstep; find
 * theirs waiting next time you play" — and neither town can ever be written by
 * anyone but its owner.
 */

import './dayEvents'
import { bus } from './events'
import { save } from './save'
import type { SaveSlot } from './types'

export interface GiftEntry {
  /** Stable id: `${fromSlot}-${day}-${n}`. The claim ledger is keyed on it. */
  readonly id: string
  readonly fromSlot: SaveSlot
  readonly toSlot: SaveSlot
  readonly itemId: string
  readonly count: number
  /** In-world day it was left. Cosmetic. */
  readonly day: number
}

export interface GiftsSaveState {
  /** Gifts I have left for the other kid. Read by THEM, never written by them. */
  outbox: GiftEntry[]
  /** Gifts I have taken in. Written only by me. */
  inbox: GiftEntry[]
  /** Ids I have already claimed, so a gift arrives exactly once. */
  claimedIds: string[]
}

const isSlot = (v: unknown): v is SaveSlot => v === 'a' || v === 'b'

function toEntry(raw: unknown): GiftEntry | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  const id = typeof r['id'] === 'string' ? r['id'] : ''
  const itemId = typeof r['itemId'] === 'string' ? r['itemId'] : ''
  const count = Math.round(Number(r['count']))
  if (id === '' || itemId === '' || !Number.isFinite(count) || count <= 0) return null
  if (!isSlot(r['fromSlot']) || !isSlot(r['toSlot'])) return null
  const day = Math.round(Number(r['day']))
  return {
    id,
    fromSlot: r['fromSlot'],
    toSlot: r['toSlot'],
    itemId,
    count,
    day: Number.isFinite(day) ? Math.max(1, day) : 1,
  }
}

class Gifts {
  private outbox: GiftEntry[] = []
  private inbox: GiftEntry[] = []
  private claimed = new Set<string>()
  private seq = 0

  /** Everything I have left for my brother. */
  sent(): readonly GiftEntry[] {
    return this.outbox
  }
  /** Everything waiting for me. */
  waiting(): readonly GiftEntry[] {
    return this.inbox
  }

  /**
   * Leave a gift on the other kid's doorstep. WRITES ONLY MY OWN SLOT.
   * Refused during a visit — a visitor's live state is the other kid's town, so
   * there is nothing safe to write.
   */
  leave(toSlot: SaveSlot, itemId: string, count: number, day: number): GiftEntry | null {
    if (save.visiting) return null
    const from = save.activeSlot
    if (toSlot === from || itemId === '' || count <= 0) return null
    const entry: GiftEntry = {
      id: `${from}-${Math.max(1, Math.round(day))}-${this.seq++}`,
      fromSlot: from,
      toSlot,
      itemId,
      count: Math.round(count),
      day: Math.max(1, Math.round(day)),
    }
    this.outbox.push(entry)
    bus.emit('gift:left', { toSlot, itemId: entry.itemId, count: entry.count })
    save.request('gift:left')
    return entry
  }

  /**
   * READ the other slot and file anything addressed to me. Writes only my slot.
   * Idempotent: the claim ledger means the same gift never arrives twice, even
   * if the other kid never plays again.
   *
   * Returns what newly arrived, so the HUD can show it on the doorstep.
   */
  claimFrom(otherSlot: SaveSlot): GiftEntry[] {
    const me = save.activeSlot
    if (otherSlot === me) return []
    const sections = save.peekSections(otherSlot)
    const blob = sections?.['gifts']
    if (blob === undefined || typeof blob.data !== 'object' || blob.data === null) return []
    const outbox = (blob.data as Record<string, unknown>)['outbox']
    if (!Array.isArray(outbox)) return []
    const arrived: GiftEntry[] = []
    for (const raw of outbox) {
      const e = toEntry(raw)
      if (e === null) continue
      if (e.toSlot !== me) continue
      if (this.claimed.has(e.id)) continue
      this.claimed.add(e.id)
      this.inbox.push(e)
      arrived.push(e)
      bus.emit('gift:received', { fromSlot: e.fromSlot, itemId: e.itemId, count: e.count })
    }
    if (arrived.length > 0) save.request('gift:claimed')
    return arrived
  }

  /** The kid opened a gift. Removes it from the doorstep; the ledger keeps the id. */
  open(id: string): GiftEntry | null {
    const i = this.inbox.findIndex((e) => e.id === id)
    if (i < 0) return null
    const [e] = this.inbox.splice(i, 1)
    save.request('gift:opened')
    return e ?? null
  }

  snapshot(): GiftsSaveState {
    return {
      outbox: [...this.outbox],
      inbox: [...this.inbox],
      claimedIds: [...this.claimed].sort(),
    }
  }

  restore(state: GiftsSaveState): void {
    this.outbox = (state.outbox ?? []).map(toEntry).filter((e): e is GiftEntry => e !== null)
    this.inbox = (state.inbox ?? []).map(toEntry).filter((e): e is GiftEntry => e !== null)
    this.claimed = new Set((state.claimedIds ?? []).filter((s) => typeof s === 'string'))
    this.seq = this.outbox.length
  }

  resetForTest(): void {
    this.outbox = []
    this.inbox = []
    this.claimed.clear()
    this.seq = 0
  }
}

export const gifts = new Gifts()
